import { useState, useRef, useEffect } from 'react';
import type { PlayerAssignment, ValidationError, PuzzleRole } from '../../types/game';

interface PlayerCardProps {
  assignment: PlayerAssignment;
  onSetRole: (seat: number, role: PuzzleRole) => void;
  errors: ValidationError[];
  isFocused: boolean;
  onFocus: (seat: number) => void;
  onBlur: () => void;
}

const ROLE_OPTIONS: { value: PuzzleRole; label: string }[] = [
  { value: 'grey',       label: 'Неизвестно' },
  { value: 'red',        label: 'Мирный' },
  { value: 'sheriff',    label: 'Шериф' },
  { value: 'black',      label: 'Мафия' },
  { value: 'maybe-black',label: 'Возможно мафия' },
  { value: 'maybe-red',  label: 'Возможно мирный' },
];

const CARD_STYLE: Record<PuzzleRole, string> = {
  'grey':        'border-mafia-border bg-mafia-card',
  'red':         'border-red-700/50 bg-red-950/25',
  'sheriff':     'border-mafia-gold/60 bg-mafia-gold/5',
  'black':       'border-mafia-crimson/60 bg-red-950/20',
  'maybe-black': 'border-dashed border-mafia-crimson/35 bg-red-950/10',
  'maybe-red':   'border-dashed border-red-700/35 bg-red-950/10',
};

// Button label (on the card — can be subtle)
const LABEL_STYLE: Record<PuzzleRole, string> = {
  'grey':        'text-mafia-muted',
  'red':         'text-red-300',
  'sheriff':     'text-mafia-gold',
  'black':       'text-mafia-crimson',
  'maybe-black': 'text-mafia-crimson/70',
  'maybe-red':   'text-red-300/70',
};

// Dropdown option text — must be clearly readable on dark solid background
const DROPDOWN_LABEL_STYLE: Record<PuzzleRole, string> = {
  'grey':        'text-mafia-paper',
  'red':         'text-red-300',
  'sheriff':     'text-mafia-gold',
  'black':       'text-[#e05555]',
  'maybe-black': 'text-[#c07070]',
  'maybe-red':   'text-[#b08080]',
};

const ROLE_ICON: Record<PuzzleRole, string> = {
  'grey':        '?',
  'red':         '●',
  'sheriff':     '★',
  'black':       '■',
  'maybe-black': '◌',
  'maybe-red':   '○',
};

const STATUS_BADGE = {
  alive:   null,
  killed:  { label: 'Убит',   cls: 'bg-mafia-crimson/20 text-mafia-crimson border border-mafia-crimson/30' },
  evicted: { label: 'Выгнан', cls: 'bg-amber-900/20 text-amber-400 border border-amber-700/30' },
};

export function PlayerCard({ assignment, onSetRole, errors, isFocused, onFocus, onBlur }: PlayerCardProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const hasError = errors.some((e) => e.affectedSeats.includes(assignment.seat));
  const badge = STATUS_BADGE[assignment.status];
  const isDead = assignment.status !== 'alive';
  const role = assignment.role;
  const currentOption = ROLE_OPTIONS.find((o) => o.value === role)!;

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function select(r: PuzzleRole) {
    onSetRole(assignment.seat, r);
    setOpen(false);
  }

  return (
    <div
      ref={ref}
      className={`
        relative rounded border transition-colors select-none
        ${CARD_STYLE[role]}
        ${hasError ? 'ring-2 ring-mafia-crimson/70' : ''}
        ${isFocused ? 'ring-2 ring-mafia-gold/50' : ''}
        ${isDead ? 'opacity-60' : ''}
        ${open ? 'z-50' : ''}
      `}
      onMouseEnter={() => onFocus(assignment.seat)}
      onMouseLeave={() => { if (!open) onBlur(); }}
    >
      {/* Watermark */}
      <div className="absolute top-1 left-2 font-display text-3xl font-bold text-white/5 leading-none pointer-events-none">
        {assignment.seat}
      </div>

      <div className="p-2.5 flex flex-col gap-1.5">
        {/* Seat + status */}
        <div className="flex items-center justify-between">
          <span className="font-sans text-xs text-mafia-paper-dim">№ {assignment.seat}</span>
          {badge && (
            <span className={`text-[9px] font-sans px-1.5 py-0.5 rounded ${badge.cls}`}>
              {badge.label}
            </span>
          )}
        </div>

        {/* Dropdown trigger */}
        <button
          onClick={() => setOpen((o) => !o)}
          className={`
            w-full flex items-center justify-between gap-1 py-1.5 px-2 rounded
            text-xs transition-colors cursor-pointer
            ${isDead ? 'opacity-70' : 'hover:brightness-110'}
            ${role === 'grey' ? 'bg-mafia-border/30' : 'bg-black/20'}
            ${LABEL_STYLE[role]}
          `}
        >
          <span className="flex items-center gap-1.5">
            <span className="text-sm leading-none">{ROLE_ICON[role]}</span>
            <span className="font-sans">{currentOption.label}</span>
          </span>
          <span className="text-mafia-muted/60 text-[10px]">▾</span>
        </button>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute top-full left-0 right-0 z-30 mt-0.5 rounded border border-mafia-gold/25 bg-[#1a1612] shadow-[0_8px_24px_rgba(0,0,0,0.7)]">
          {ROLE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => select(opt.value)}
              className={`
                w-full text-left px-3 py-2.5 text-xs font-sans flex items-center gap-2.5
                transition-colors
                ${opt.value === role
                  ? 'bg-mafia-gold/10 border-l-2 border-mafia-gold/50'
                  : 'hover:bg-white/5 border-l-2 border-transparent'
                }
                ${DROPDOWN_LABEL_STYLE[opt.value]}
              `}
            >
              <span className="w-3.5 text-center text-sm leading-none shrink-0">{ROLE_ICON[opt.value]}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
